import type React from "react";
import { useState } from "react";
import "./DeveloperMode.css";
import { usePost } from "@albertine/api-client";
import type { CreatePublicShareLinkRequest } from "@albertine/types/openapi/lmt";
import { useAuth } from "react-oidc-context";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import Button from "../components/Common/Buttons";
import { Flex } from "../components/Common/Flex";
import Input from "../components/Common/Input";
import Label from "../components/Common/Label";
import { Title } from "../components/ComponentLibrary/LibraryBuildingBlocks";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { developerFixGoogleUrlOnCatalogItems } from "../FirebaseBackendActions";
import { copyTextToClipboard } from "../utils/window.util";
import { FindRevolutMembersByPromoCodes } from "./FindRevolutMembersByPromoCodes";
import { TestTranslation } from "./TestTranslation";

function DeveloperModeSection({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Flex.Column className="developer-mode__section">
            <h2>{title}</h2>
            <Flex.Column gap="medium">{children}</Flex.Column>
        </Flex.Column>
    );
}

function SyncAgentsToKeycloak() {
    const { mutate, isPending } = usePost("syncAgentsToKeycloak");

    return (
        <Button.Secondary
            label="Sync all agents to Keycloak"
            showLoadingSpinner={isPending}
            onClick={() => {
                mutate({});
            }}
        />
    );
}

function SyncAllThemesToOpenSearch() {
    const [startTime, setStartTime] = useState<number>(0);
    const [result, setResult] = useState<string>("");
    const { mutate, isPending } = usePost("syncAllThemesToOpenSearch", {
        onSuccess: () => {
            setResult(
                `Theme reindexing in OpenSearch completed in ${Date.now() - startTime}ms`,
            );
        },
        onError: (error) => {
            setResult(
                `Theme reindexing failed after ${Date.now() - startTime}ms. Error: ${error}`,
            );
        },
    });

    return (
        <>
            <Button.Secondary
                label="Sync all themes to OpenSearch"
                showLoadingSpinner={isPending}
                onClick={() => {
                    mutate({});
                    setStartTime(Date.now());
                }}
            />

            <Label.Large.N10>Results</Label.Large.N10>

            {isPending && <LoadingSpinner />}
            {!isPending && <pre>{result}</pre>}
        </>
    );
}

function SyncMembersToKeycloak() {
    const [result, setResult] = useState<string>("");
    const { mutate, isPending } = usePost("syncAllMembersToKeycloak", {
        onSuccess: (result) => {
            setResult(JSON.stringify(result, undefined, 2));
        },
        onError: (error) => {
            setResult(JSON.stringify(error, undefined, 2));
        },
    });

    return (
        <>
            <Button.Secondary
                label="Sync all members to Keycloak"
                showLoadingSpinner={isPending}
                onClick={() => {
                    mutate({});
                }}
            />

            <Label.Large.N10>Results</Label.Large.N10>

            {isPending && <LoadingSpinner />}
            {!isPending && <pre>{result}</pre>}
        </>
    );
}

function EnsureAppReviewMembers() {
    const [result, setResult] = useState<string>("");
    const { mutate, isPending } = usePost("ensureAppReviewMembers", {
        onSuccess: (result) => {
            setResult(JSON.stringify(result, undefined, 2));
        },
        onError: (error) => {
            setResult(JSON.stringify(error, undefined, 2));
        },
    });

    return (
        <>
            <Button.Secondary
                label="Ensure app review members"
                showLoadingSpinner={isPending}
                onClick={() => {
                    mutate({});
                }}
            />

            <Label.Large.N10>Results</Label.Large.N10>

            {isPending && <LoadingSpinner />}
            {!isPending && <pre>{result}</pre>}
        </>
    );
}

function parseIdsFromTextArea(raw: string): string[] {
    return Array.from(
        new Set(
            raw
                .split(/[\n,]+/)
                .map((id) => id.trim())
                .filter(Boolean),
        ),
    );
}

type BroadcastValidationErrors = {
    message?: string;
    memberIds?: string;
    catalogItemIds?: string;
};

function BroadcastMessagesToMembers() {
    const oidc = useAuth();
    const [message, setMessage] = useState("");
    const [memberIdsText, setMemberIdsText] = useState("");
    const [catalogItemIdsText, setCatalogItemIdsText] = useState("");
    const [validationErrors, setValidationErrors] = useState<BroadcastValidationErrors>(
        {},
    );

    const { mutate, isPending, data, error } = usePost("broadcastMessagesToMembers");

    const clearFieldError = (field: keyof BroadcastValidationErrors) => {
        setValidationErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleSendBroadcastMessage = () => {
        const memberIds = parseIdsFromTextArea(memberIdsText);
        const catalogItemIds = parseIdsFromTextArea(catalogItemIdsText);
        const trimmedMessage = message.trim();
        const errors: BroadcastValidationErrors = {};

        if (!trimmedMessage) {
            errors.message = "Message is required.";
        }
        if (memberIds.length === 0) {
            errors.memberIds = "At least one member ID is required.";
        } else if (memberIds.length > 200) {
            errors.memberIds = "Maximum 200 member IDs per send.";
        }
        if (catalogItemIds.length > 10) {
            errors.catalogItemIds = "Maximum 10 catalog item IDs per send.";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        const keycloakToken = oidc.user?.access_token;
        if (!keycloakToken) {
            window.alert("You must be logged in to send broadcast messages.");
            return;
        }

        const catalogSuffix =
            catalogItemIds.length > 0
                ? ` with ${catalogItemIds.length} catalog item(s)`
                : "";
        if (
            !window.confirm(
                `Send message to ${memberIds.length} member(s)${catalogSuffix} as each member's assigned agent?`,
            )
        ) {
            return;
        }

        setValidationErrors({});
        mutate(
            {
                json: {
                    message: trimmedMessage,
                    ...(catalogItemIds.length > 0 && { catalogItemIds }),
                    memberIds,
                },
            },
            {
                onSuccess: () => {
                    setMessage("");
                    setMemberIdsText("");
                    setCatalogItemIdsText("");
                },
            },
        );
    };

    return (
        <>
            <Field data-invalid={!!validationErrors.message}>
                <FieldLabel htmlFor="broadcast-message">
                    Message (use {"{greeting}"} for member preferred greeting)
                </FieldLabel>
                <Textarea
                    id="broadcast-message"
                    value={message}
                    aria-invalid={!!validationErrors.message}
                    onChange={(e) => {
                        setMessage(e.target.value);
                        clearFieldError("message");
                    }}
                />
                <FieldError errors={[{ message: validationErrors.message }]} />
            </Field>
            <Field data-invalid={!!validationErrors.catalogItemIds}>
                <FieldLabel htmlFor="broadcast-catalog-item-ids">
                    Catalog item IDs (one per line or comma-separated, max 10)
                </FieldLabel>
                <Textarea
                    id="broadcast-catalog-item-ids"
                    value={catalogItemIdsText}
                    aria-invalid={!!validationErrors.catalogItemIds}
                    onChange={(e) => {
                        setCatalogItemIdsText(e.target.value);
                        clearFieldError("catalogItemIds");
                    }}
                />
                <FieldError errors={[{ message: validationErrors.catalogItemIds }]} />
            </Field>
            <Field data-invalid={!!validationErrors.memberIds}>
                <FieldLabel htmlFor="broadcast-member-ids">
                    Member IDs (one per line or comma-separated, max 200)
                </FieldLabel>
                <Textarea
                    id="broadcast-member-ids"
                    value={memberIdsText}
                    aria-invalid={!!validationErrors.memberIds}
                    onChange={(e) => {
                        setMemberIdsText(e.target.value);
                        clearFieldError("memberIds");
                    }}
                />
                <FieldError errors={[{ message: validationErrors.memberIds }]} />
            </Field>
            <Button.Secondary
                label="Send broadcast message"
                showLoadingSpinner={isPending}
                onClick={handleSendBroadcastMessage}
            />

            <Label.Large.N10>Results</Label.Large.N10>

            {isPending && <LoadingSpinner />}
            {!isPending && (
                <pre>
                    {(data ?? error) ? JSON.stringify(data ?? error, undefined, 2) : ""}
                </pre>
            )}
        </>
    );
}

function SyncSingleMemberToKeycloak() {
    const [memberId, setMemberId] = useState<string>("");
    const [result, setResult] = useState<string>("");
    const { mutate, isPending } = usePost("syncSingleMemberToKeycloak", {
        onSuccess: (result) => {
            setResult(JSON.stringify(result, undefined, 2));
        },
        onError: (error) => {
            setResult(JSON.stringify(error, undefined, 2));
        },
    });

    return (
        <>
            <Input.PersistentTextField
                label="Member ID"
                value={memberId}
                onChange={(value) => {
                    setMemberId(value);
                }}
            />
            <Button.Secondary
                label="Sync member to Keycloak"
                showLoadingSpinner={isPending}
                onClick={() => {
                    mutate({ param: { memberId } });
                }}
            />

            <Label.Large.N10>Results</Label.Large.N10>

            {isPending && <LoadingSpinner />}
            {!isPending && <pre>{result}</pre>}
        </>
    );
}

export default function DeveloperMode() {
    const { mutate: closeConfirmedRequests, isPending: closeConfirmedRequestsPending } =
        usePost("closeConfirmedRequests");
    const { mutateAsync: createShareLink } = usePost("createShareLink");
    const [requestId, setRequestId] = useState<string>("");
    const [bookingId, setBookingId] = useState<string>("");

    async function getShareUrl(params: CreatePublicShareLinkRequest): Promise<string> {
        try {
            const response = await createShareLink({ json: params });
            return response.url;
        } catch {
            alert("Getting link to proposal failed");
            return "";
        }
    }

    return (
        <div className="developer-screen">
            <div className="developer-mode">
                <Title>Developer Mode</Title>

                <DeveloperModeSection title="Get proposal share url">
                    <Input.PersistentTextField
                        label="Request ID"
                        value={requestId}
                        onChange={(value) => {
                            setRequestId(value);
                        }}
                    />
                    <Button.Secondary
                        label="Get public proposal share url"
                        onClick={async () => {
                            const url = await getShareUrl({
                                resourceType: "proposal",
                                resourceId: requestId,
                            });

                            if (url.length !== 0) {
                                copyTextToClipboard(url);
                                alert(
                                    "Link copied to clipboard. You can now share it.",
                                );
                            }
                        }}
                    />
                </DeveloperModeSection>

                <DeveloperModeSection title="Get booking url">
                    <Input.PersistentTextField
                        label="Confirmed booking ID"
                        value={bookingId}
                        onChange={(value) => {
                            setBookingId(value);
                        }}
                    />
                    <Button.Secondary
                        label="Get public confirmed booking share url"
                        onClick={async () => {
                            const url = await getShareUrl({
                                resourceType: "booking",
                                resourceId: bookingId,
                            });

                            if (url.length !== 0) {
                                copyTextToClipboard(url);
                                alert(
                                    "Link copied to clipboard. You can now share it.",
                                );
                            }
                        }}
                    />
                </DeveloperModeSection>

                <DeveloperModeSection title="Run closing confirmed requests">
                    <p>
                        Trigger{" "}
                        <strong>POST /lmt/developer/close-confirmed-requests</strong>.
                        Scan all confirmed requests and update requests if the bookings
                        end or start time (which ever is available) in a request + 6
                        hours is in the past
                    </p>
                    <Button.Secondary
                        label={
                            closeConfirmedRequestsPending
                                ? "Closing confirmed requests..."
                                : "Run close confirmed requests"
                        }
                        onClick={() => closeConfirmedRequests({})}
                        disabled={closeConfirmedRequestsPending}
                    />
                </DeveloperModeSection>

                <DeveloperModeSection title="Fix missing google URLs on catalog items">
                    <p>
                        Scan all catalog items which do not have a valid google URL
                        (non-existing or null) and fetch a valid google URL for them.
                        Updates max 300 items per run. See the cloud function logs for
                        more details.
                    </p>
                    <Button.Secondary
                        label="Run Firebase Function"
                        onClick={async () => {
                            window.alert(
                                "Starting the execution. Can take up to few minutes...",
                            );

                            const { data } =
                                await developerFixGoogleUrlOnCatalogItems();

                            const createLink = (url: string) => {
                                const link = document.createElement("a");
                                link.href = url;
                                link.innerText = url;
                                link.target = "_blank";
                                return link;
                            };

                            data.forEach((result) => {
                                const catalogLink = `catalog-beta/${result.id}`;
                                const div = document.createElement("div");
                                div.appendChild(createLink(catalogLink));
                                if (result.id && result.googleUrl) {
                                    const span = document.createElement("span");
                                    span.innerHTML = " linked to ";
                                    div.appendChild(span);
                                    div.appendChild(createLink(result.googleUrl));
                                } else {
                                    const span = document.createElement("span");
                                    span.innerHTML =
                                        " Could not find a valid google URL";
                                }
                                const element = document.getElementById(
                                    "developer-mode__fix-google-url-result",
                                );
                                element?.appendChild(div);
                            });
                        }}
                    />
                    <div id="developer-mode__fix-google-url-result" />
                </DeveloperModeSection>

                <DeveloperModeSection title="Sync themes to OpenSearch">
                    <p>
                        Syncs all published themes (including content configuration and
                        city guides) to the respective OpenSearch index.
                    </p>
                    <SyncAllThemesToOpenSearch />
                </DeveloperModeSection>

                <DeveloperModeSection title="Sync agents to Keycloak">
                    <p>Creates or updates all agent users in the Keycloak LMT realm.</p>
                    <SyncAgentsToKeycloak />
                </DeveloperModeSection>

                <DeveloperModeSection title="Sync all members to Keycloak">
                    <p>
                        Creates or updates all member users in the respective Keycloak
                        realm.
                    </p>
                    <SyncMembersToKeycloak />
                </DeveloperModeSection>

                <DeveloperModeSection title="Sync single member to Keycloak">
                    <p>
                        Creates or updates a single member user in the respective
                        Keycloak realm.
                    </p>
                    <SyncSingleMemberToKeycloak />
                </DeveloperModeSection>

                <DeveloperModeSection title="Ensure app review members">
                    <p>
                        Creates or repairs the App Store and Play Store review member of
                        every tenant, the same as the daily job. Use it to provision the
                        members right after a deploy or for a new tenant.
                    </p>
                    <EnsureAppReviewMembers />
                </DeveloperModeSection>

                <DeveloperModeSection title="Send broadcast message">
                    <p>
                        Send a chat message to listed members in their general
                        conversation, from you as the logged-in agent. Members receive a
                        normal message push notification. Local environment writes
                        messages but skips FCM.
                    </p>
                    <BroadcastMessagesToMembers />
                </DeveloperModeSection>

                <DeveloperModeSection title="Find Revolut members by promo codes">
                    <p>
                        Upload the promo codes CSV shared by Revolut (it needs a
                        &quot;Promo Code&quot; column). Downloads a CSV with the tenant,
                        first name, last name, email and phone number of each member who
                        redeemed one of the codes.
                    </p>
                    <FindRevolutMembersByPromoCodes />
                </DeveloperModeSection>

                <DeveloperModeSection title="Test translation">
                    <p>
                        Translate a text with the chat LLM or Google Translate NMT,
                        using the same translators as member chat.
                    </p>
                    <TestTranslation />
                </DeveloperModeSection>
            </div>
        </div>
    );
}
